import { useCallback, useMemo, useState } from 'react';

import { stopEventPropagation } from '@lyearn/core/utils/events';
import { useTranslation } from '@lyearn/i18n';
import { ArticleOutline, Trash2Outline } from '@lyearn/icons';
import { TypographyWithTooltip } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { InfoPill, LinkButton, Loader, Stack, Tooltip, Typography, useSnackbar } from '@lyearn/ui';
import * as SidebarAccordion from '@/components/Panel/SidebarAccordion';
import Tabs from '@/components/Tabs';
import { FeatureFlags, useFeatureFlags } from '@/helper/FeatureFlags';
import { NoteReviewStatusTypeEnum, NoteStatusTypeEnum, NoteTypeEnum } from '@/types/schema';

import { useDeleteNoteMutation } from '../../graphql/mutations/generated/deleteNote';
import { useNotesQuery } from '../../graphql/queries/generated/notes';
import { NoteFragment } from '../../graphql/queries/generated/notes';
import { KnowledgeDocsTabs, NotesViewMode } from '../../types';
import { getKnowledgeDocsTabs } from '../../utils';
import DeleteModal from '../DeleteModal';

export const Notes = ({
  currentNote,
  setCurrentNote,
  onNewNote,
  switchView,
  viewMode,
  currentNotesTab,
  setCurrentNotesTab,
}: {
  currentNote: NoteFragment | null;
  setCurrentNote: React.Dispatch<React.SetStateAction<NoteFragment | null>>;
  onNewNote: () => void;
  switchView: (view: NotesViewMode) => void;
  viewMode: NotesViewMode;
  currentNotesTab: KnowledgeDocsTabs;
  setCurrentNotesTab: React.Dispatch<React.SetStateAction<KnowledgeDocsTabs>>;
}) => {
  const { t } = useTranslation(['Common', 'Coach']);
  const [, deleteNote] = useDeleteNoteMutation();
  const [isDeletingId, setIsDeletingId] = useState<string | null>(null);
  const { showSnackbar, showErrorSnackbar } = useSnackbar();
  const [showDeletionModal, setShowDeletionModal] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState<string | null>(null);
  const { [FeatureFlags.EnableKnowledgeBaseTeachMode]: isTeachModeFeatureEnabled } =
    useFeatureFlags();

  const onOpenNote = useCallback(
    (note: NoteFragment) => {
      setCurrentNote(note);
    },
    [setCurrentNote],
  );

  const [{ data, fetching }] = useNotesQuery({
    variables: {
      input: {
        paginationProps: {
          first: -1,
        },
        filters: {
          ids: [],
          types: [
            viewMode === NotesViewMode.Notes
              ? NoteTypeEnum.KnowledgeBase
              : NoteTypeEnum.KnowledgeDoc,
          ],
          // my docs tab
          ...(viewMode === NotesViewMode.KnowledgeAssets &&
          currentNotesTab === KnowledgeDocsTabs.MyDocs
            ? { status: [NoteStatusTypeEnum.Draft, NoteStatusTypeEnum.Published] }
            : {}),
          // pending approval tab
          ...(viewMode === NotesViewMode.KnowledgeAssets &&
          currentNotesTab === KnowledgeDocsTabs.PendingApproval
            ? {
                reviewStatus: [
                  NoteReviewStatusTypeEnum.InReview,
                  NoteReviewStatusTypeEnum.Rejected,
                ],
              }
            : {}),
          // approved by me tab
          ...(viewMode === NotesViewMode.KnowledgeAssets &&
          currentNotesTab === KnowledgeDocsTabs.Approved
            ? { reviewStatus: [NoteReviewStatusTypeEnum.Approved] }
            : {}),
        },
      },
    },
  });

  const notes = useMemo(
    () => data?.notes?.edges?.map((edge) => edge?.node).filter((note) => note?.title?.length) || [],
    [data?.notes?.edges],
  );

  const handleDeleteNote = useCallback((noteId: string) => {
    setShowDeletionModal(true);
    setDeletingNoteId(noteId);
  }, []);

  const onDeleteNote = useCallback(
    (id: string) => {
      setIsDeletingId(id);
      setShowDeletionModal(false);
      setDeletingNoteId(null);
      deleteNote({
        input: {
          id,
        },
      })
        .then((res) => {
          if (res?.data?.deleteNote?.success) {
            // setCurrentNote(notes[0]);
            showSnackbar({ message: t('Common:KnowledgeBase.NoteDeleted') });
          } else {
            showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
          }
          setIsDeletingId(null);
        })
        .catch(() => {
          showErrorSnackbar({ message: t('Common:Error.SomethingWentWrong') });
          setIsDeletingId(null);
        });
    },
    [deleteNote, notes, setCurrentNote, showErrorSnackbar, showSnackbar, t],
  );

  const getPill = (
    status?: NoteStatusTypeEnum | null,
    reviewStatus?: NoteReviewStatusTypeEnum | null,
  ) => {
    if (reviewStatus === NoteReviewStatusTypeEnum.InReview) {
      return (
        <InfoPill
          appearance="warning"
          label={t('Coach:NotesStatus.InReview')}
          size="medium"
          type="text"
        />
      );
    }
    if (reviewStatus === NoteReviewStatusTypeEnum.Approved) {
      return (
        <InfoPill
          appearance="success"
          label={t('Coach:NotesStatus.Approved')}
          size="medium"
          type="text"
        />
      );
    }
    if (reviewStatus === NoteReviewStatusTypeEnum.Rejected) {
      return (
        <InfoPill
          appearance="danger"
          label={t('Coach:NotesStatus.Rejected')}
          size="medium"
          type="text"
        />
      );
    }
    if (status === NoteStatusTypeEnum.Draft) {
      return (
        <InfoPill
          appearance="blue"
          label={t('Coach:NotesStatus.Draft')}
          size="medium"
          type="text"
        />
      );
    }
    return null;
  };

  const renderNotes = () => {
    if (fetching) {
      return (
        <Stack className="mx-auto mt-48 mb-0 min-h-[120px] items-center justify-center">
          <Loader size={20} />
        </Stack>
      );
    }

    if (notes.length === 0) {
      return (
        <Stack className="px-12 py-[9px]">
          <Typography className="text-text-placeholder" variant="label-02">
            {t('Common:KnowledgeBase.NoNotes')}
          </Typography>
        </Stack>
      );
    }

    return (
      <>
        <SidebarAccordion.Main isExpanded={true}>
          {notes.map((note) => {
            const isCurrentNote = note.id === currentNote?.id;
            return (
              <Stack
                key={note.id}
                alignItems="center"
                className={cn(
                  'px-12 py-[9px] hover:bg-bg-hover rounded-8 group cursor-pointer w-full',
                  isCurrentNote && 'bg-interactive-secondary-hover',
                )}
                justifyContent="between"
                onClick={() => onOpenNote(note)}>
                <Stack
                  alignItems="start"
                  className="flex-1"
                  direction="row"
                  gapX={10}
                  justifyContent="start">
                  <Stack className="flex-shrink-0">
                    <ArticleOutline
                      fillColorToken={
                        viewMode === NotesViewMode.Notes ? 'icon-primary' : 'icon-accent'
                      }
                      height={20}
                      width={20}
                    />
                  </Stack>
                  <Stack alignItems="start" className="w-full" direction="col" gapY={4}>
                    <Stack
                      alignItems="center"
                      className="w-full"
                      direction="row"
                      gapX={8}
                      justifyContent="between">
                      <TypographyWithTooltip
                        className="max-w-[30rem]"
                        numberOfLines={1}
                        variant="heading-xxs">
                        {note.title}
                      </TypographyWithTooltip>
                      {viewMode === NotesViewMode.KnowledgeAssets
                        ? getPill(note?.status, note.reviewConfig?.reviewStatus)
                        : null}
                    </Stack>
                    {note.description || note.plainText ? (
                      <Typography numberOfLines={2} variant="body-short-02">
                        {note.description || note.plainText}
                      </Typography>
                    ) : null}
                  </Stack>
                </Stack>
                {isDeletingId === note.id ? (
                  <Stack className="flex-shrink-0">
                    <Loader size={24} />
                  </Stack>
                ) : (
                  <Stack className="opacity-0 group-hover:opacity-100 flex-shrink-0">
                    <Tooltip title={t('Common:Actions.Delete')}>
                      <Trash2Outline
                        className="opacity-0 group-hover:opacity-100"
                        fillColorToken="icon-primary"
                        height={20}
                        width={20}
                        onClick={(event) => {
                          stopEventPropagation(event);
                          handleDeleteNote(note.id);
                        }}
                      />
                    </Tooltip>
                  </Stack>
                )}
              </Stack>
            );
          })}
        </SidebarAccordion.Main>
      </>
    );
  };

  const tabs = useMemo(() => getKnowledgeDocsTabs(t), []);

  return (
    <Stack direction="col" gap={16}>
      {isTeachModeFeatureEnabled ? (
        <Stack
          alignItems="center"
          className="rounded-full bg-bg-tertiary p-2 w-fit"
          direction="row"
          gapX={2}>
          <LinkButton
            className={cn('rounded-full px-12 py-4 hover:!bg-bg-secondary', {
              'bg-bg-primary !text-text-primary': viewMode === NotesViewMode.Notes,
            })}
            onClick={() => switchView(NotesViewMode.Notes)}>
            {t('Common:KnowledgeBase.Notes')}
          </LinkButton>
          <LinkButton
            className={cn('rounded-full px-12 py-4 hover:!bg-bg-secondary', {
              'bg-bg-primary !text-text-primary': viewMode === NotesViewMode.KnowledgeAssets,
            })}
            onClick={() => switchView(NotesViewMode.KnowledgeAssets)}>
            {t('Coach:Tabs.KnowledgeAssets')}
          </LinkButton>
        </Stack>
      ) : null}

      {viewMode === NotesViewMode.KnowledgeAssets && (
        <Tabs className="mt-8" tabs={tabs} value={currentNotesTab} onChange={setCurrentNotesTab} />
      )}

      <Stack className="mt-8" direction="col" gapY={2}>
        {renderNotes()}
      </Stack>

      <DeleteModal
        description={t('Common:KnowledgeBase.DeleteNoteDescription')}
        isSubmitting={false}
        open={showDeletionModal}
        title={t('Common:KnowledgeBase.DeleteNoteTitle')}
        onClose={() => setShowDeletionModal(false)}
        onDelete={() => onDeleteNote(deletingNoteId!)}
      />
    </Stack>
  );
};
